/**
 * The Business Units route table (FSD 1.2.4).
 *
 * `web/src/router/index.ts` and `web/src/nav/parts/p01-enterprise.ts` are
 * shared files several sibling C14 packets also touch and this packet does
 * NOT own — the orchestrator spreads `BUSINESS_UNIT_ROUTES` into the router
 * and adds the nav entry once every packet in this batch has landed.
 *
 * Every route is gated on the **read** permission: creating, activating,
 * deactivating, assigning functions/reference sets/ledger alignments and
 * recording service-provider relationships are all checked on the action
 * itself, so an Auditor holding only `foundation.business_unit.read` can open
 * the catalogue and any unit by id and change nothing.
 */

import type { RouteRecordRaw } from 'vue-router'

import { BUSINESS_UNIT_PERMISSIONS } from '@/modules/enterprise/business-units/permissions'

export const BUSINESS_UNITS_BASE = '/enterprise/business-units'

export function businessUnitsRoute(): string {
  return BUSINESS_UNITS_BASE
}

export function businessUnitRoute(id: number): string {
  return `${BUSINESS_UNITS_BASE}/${id}`
}

export function serviceProvidersRoute(): string {
  return `${BUSINESS_UNITS_BASE}/service-providers`
}

export function securityScopesRoute(): string {
  return `${BUSINESS_UNITS_BASE}/security-scopes`
}

/*
 * The two register screens are declared before `:id(\d+)`. The numeric
 * constraint already keeps them apart, but order makes the intent obvious.
 *
 * Security Scopes is gated on `security_scope`, not `read`: it lists who can
 * reach which unit's data across the tenant, which FSD 1.2.4.1 gives to the
 * Security Administrator. The per-unit preview tab uses the same grant.
 */
export const BUSINESS_UNIT_ROUTES: RouteRecordRaw[] = [
  {
    path: BUSINESS_UNITS_BASE,
    name: 'enterprise-business-units',
    component: () => import('@/modules/enterprise/business-units/BusinessUnitsListView.vue'),
    meta: {
      titleKey: 'enterprise.businessUnits.routeTitles.list',
      permission: BUSINESS_UNIT_PERMISSIONS.read,
    },
  },
  {
    path: serviceProvidersRoute(),
    name: 'enterprise-service-providers',
    component: () => import('@/modules/enterprise/business-units/ServiceProvidersView.vue'),
    meta: {
      titleKey: 'enterprise.businessUnits.routeTitles.serviceProviders',
      permission: BUSINESS_UNIT_PERMISSIONS.read,
    },
  },
  {
    path: securityScopesRoute(),
    name: 'enterprise-security-scopes',
    component: () => import('@/modules/enterprise/business-units/SecurityScopesView.vue'),
    meta: {
      titleKey: 'enterprise.businessUnits.routeTitles.securityScopes',
      permission: BUSINESS_UNIT_PERMISSIONS.securityScope,
    },
  },
  {
    path: `${BUSINESS_UNITS_BASE}/:id(\\d+)`,
    name: 'enterprise-business-unit',
    component: () => import('@/modules/enterprise/business-units/BusinessUnitView.vue'),
    meta: {
      titleKey: 'enterprise.businessUnits.routeTitles.detail',
      permission: BUSINESS_UNIT_PERMISSIONS.read,
    },
  },
]
